import { MessageSquare, Pencil, Server } from "lucide-react";
import type { ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage, Button, Dialog, DialogContent, DialogTitle, DialogDescription, cn } from "@link/ui";

import { ProfileUsername } from "./ProfileUsername";
import { CommunityTagChip, type MemberProfile } from "./MemberProfilePopover";
import { StatusDot, STATUS_LABEL } from "./StatusDot";
import { UserActionsMenu } from "./UserActionsMenu";
import { useSelf } from "../state/SelfSession";
import { profileBannerStyle } from "../lib/profile-banner";
import { FriendActionButton } from "./FriendActionButton";
import { requestDirectMessage } from "../lib/dm-navigation";

export function UserProfileModal({ open, member, onOpenChange }: { open: boolean; member: MemberProfile; onOpenChange: (open: boolean) => void }) {
  const self = useSelf();
  const isSelf = member.tag === self.identity.accountXid;
  if (!open) return null;
  const username = member.username ?? member.name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="block h-[min(760px,calc(100dvh-2rem))] max-w-5xl overflow-y-auto rounded-2xl border-cloud-300 bg-white p-0 text-cloud-700 shadow-2xl sm:rounded-3xl lg:grid lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-0 lg:overflow-hidden">
        <DialogTitle className="sr-only">Profile for {member.name}</DialogTitle>
        <DialogDescription className="sr-only">Account details, badges, and server roles for @{username}.</DialogDescription>

        <section className="min-h-0 border-b border-cloud-200 bg-cloud-50/70 lg:overflow-y-auto lg:border-b-0 lg:border-r">
          <div className="h-40" style={profileBannerStyle(member.bannerColor ?? "#ee7f9f", member.bannerMode ?? "solid", member.bannerSecondaryColor ?? "#9b7bd3")} />
          <div className="relative -mt-16 px-7 pb-8">
            <div className="relative inline-block rounded-full ring-[7px] ring-cloud-50">
              <Avatar className="h-28 w-28">
                {member.avatarUrl != null ? <AvatarImage src={member.avatarUrl} alt="" /> : null}
                <AvatarFallback className="text-3xl">{member.name.slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="absolute bottom-1 right-1 rounded-full bg-cloud-50 p-1"><StatusDot status={member.status} sizeClass="h-4 w-4" ringClass="ring-cloud-50" /></span>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <h2 className="text-2xl font-bold text-cloud-900 [overflow-wrap:anywhere]">{member.name}</h2>
            </div>
            <ProfileUsername username={username} badges={member.badges ?? []}>{member.communityTag != null ? <CommunityTagChip tag={member.communityTag} /> : null}{member.pronouns != null && member.pronouns.trim().length > 0 ? <span className="rounded-full bg-cloud-100 px-2 py-0.5 text-[10px] font-semibold text-cloud-600 ring-1 ring-cloud-200">{member.pronouns}</span> : null}</ProfileUsername>
            <p className="mt-2 inline-flex items-center gap-2 text-xs text-cloud-500"><StatusDot status={member.status} sizeClass="h-2.5 w-2.5" />{STATUS_LABEL[member.status]}</p>

            <div className="mt-5 flex items-center gap-2">
              {isSelf ? (
                <Button onClick={() => { onOpenChange(false); self.openSettings("profile"); }}><Pencil className="mr-2 h-4 w-4" /> Edit profile</Button>
              ) : (
                <><FriendActionButton accountXid={member.tag} displayName={member.name} /><Button variant="outline" size="icon" aria-label={`Message ${member.name}`} onClick={() => { onOpenChange(false); requestDirectMessage(member.tag); }}><MessageSquare className="h-4 w-4" /></Button></>
              )}
              <UserActionsMenu accountXid={member.tag} displayName={member.name} excludeSpaceXid={member.currentSpaceXid} />
            </div>

            {member.bio !== null ? <p className="mt-6 rounded-2xl border border-grape-200 bg-white px-4 py-3 text-sm italic text-cloud-700">“{member.bio}”</p> : null}
            <ProfileDetail title="Member since">{member.memberSince ?? "Not available"}</ProfileDetail>
            {member.currentSpaceXid !== undefined ? (
              <ProfileDetail title="Roles">
                <div className="flex flex-wrap gap-2">
                  {member.roles.length > 0 ? member.roles.map((role) => <span key={role.id} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs ring-1 ring-cloud-200"><span className={cn("h-2 w-2 rounded-full", role.color === undefined ? (role.dotClass ?? "bg-strawberry-400") : undefined)} style={role.color === undefined ? undefined : { backgroundColor: role.color }} />{role.label}</span>) : <span className="text-sm text-cloud-400">No server roles</span>}
                </div>
              </ProfileDetail>
            ) : null}
            <ProfileDetail title="Mutual servers">{member.mutualSpaces.length > 0 ? member.mutualSpaces.join(", ") : "No mutual servers yet"}</ProfileDetail>
          </div>
        </section>

        <section className="flex min-h-0 flex-col bg-white px-5 pb-8 pt-8 sm:px-8 lg:px-10">
          <div className="border-b border-cloud-200 pr-12 pb-5">
            <h3 className="text-lg font-semibold text-cloud-900">Profile overview</h3>
            <p className="mt-1 text-sm text-cloud-500">Public account details and the servers you share.</p>
          </div>
          <div className="min-h-0 flex-1 space-y-4 py-6 lg:overflow-y-auto">
            <OverviewCard title="Account">
              <dl className="grid gap-4 sm:grid-cols-2">
                <OverviewValue label="Username" value={`@${username}`} />
                <OverviewValue label="Status" value={STATUS_LABEL[member.status]} />
                <OverviewValue label="Pronouns" value={member.pronouns?.trim() || "Not set"} />
                <OverviewValue label="Member since" value={member.memberSince ?? "Not available"} />
              </dl>
            </OverviewCard>
            {member.bio !== null && member.bio.trim().length > 0 ? <OverviewCard title="About"><p className="text-sm leading-6 text-cloud-600">{member.bio}</p></OverviewCard> : null}
            <OverviewCard title={`Mutual servers · ${member.mutualSpaces.length}`}>
              {member.mutualSpaces.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {member.mutualSpaces.map((space) => <div key={space} className="flex items-center gap-2 rounded-xl border border-cloud-200 bg-cloud-50 px-3 py-3 text-sm font-semibold text-cloud-700"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-cloud-500 ring-1 ring-cloud-200"><Server className="h-4 w-4" /></span><span className="truncate">{space}</span></div>)}
                </div>
              ) : <p className="text-sm text-cloud-500">You do not share a server with this account.</p>}
            </OverviewCard>
          </div>
        </section>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDetail({ title, children }: { title: string; children: ReactNode }) {
  return <div className="mt-6"><h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-cloud-400">{title}</h3><div className="text-sm text-cloud-700">{children}</div></div>;
}

function OverviewCard({ title, children }: { title: string; children: ReactNode }) {
  return <section className="rounded-2xl border border-cloud-200 bg-cloud-50/70 p-5"><h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-cloud-500">{title}</h4>{children}</section>;
}

function OverviewValue({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-[11px] font-semibold uppercase tracking-wider text-cloud-400">{label}</dt><dd className="mt-1 break-words text-sm font-medium text-cloud-700">{value}</dd></div>;
}
